import { useCallback } from 'react';
import { useMutation } from '@common-fe/common-fe';
import { AxiosResponse } from 'axios';
import { capitalize, toNumber, toString } from 'lodash';

import { api } from '@/api';
import {
  API_URL,
  ICarrierConfigDto,
  ISaveCarrierConfigDto,
  ISaveCarrierConfigDtoAmountTypesEnum,
  ISaveCarrierConfigDtoPurposeTypeEnum,
  ISaveCarrierConfigDtoStatusTypeEnum,
} from '@/dtos';
import useCarrierConfigStore from '@/modules/CarrierConfig/useCarrierConfig.store';
import { ClaimAutopayType } from '@/modules/employee/employee.types';

const DATE_OVERLAPPED_MESSAGE = 'Service date periods should not overlap';
const SUBMITTER_ID_MAX_LENGTH = 40;

export enum PorposeType {
  claimCreation = 'CLAIM_CREATION',
  adjudication = 'ADJUDICATION',
}
export enum ClaimPayeeType {
  participant = 'PARTICIPANT',
  provider = 'PROVIDER',
  employeeChoice = 'EMPLOYEE_CHOICE',
}
export enum AmountTypes {
  coinsurance = 'COINSURANCE',
  copay = 'COPAY',
  patientResponsibility = 'PATIENT_RESPONSIBILITY',
  deductible = 'DEDUCTIBLE',
}

export interface CarrierConfig {
  carrierExternalId: string;
  organizationId: string;
  organizationPath: string;
  organizationName: string;
  submitterId: string;
  description: string;
  serviceDateFrom: string;
  serviceDateTo: string;
  purposeType: PorposeType;
  claimAutopayType: ClaimAutopayType;
  isDependentDeidentified: boolean;
  isDependentAutocreated: boolean;
  claimPayeeType: ClaimPayeeType;
  amountTypes: AmountTypes[];
}

const formatDataToPayload = (carrierConfig: CarrierConfig): ISaveCarrierConfigDto => ({
  carrier_external_id: carrierConfig.carrierExternalId,
  organization_id: toNumber(carrierConfig.organizationId),
  // TODO: the type is missing
  // @ts-ignore
  organization_name: carrierConfig.organizationName,
  // organization_path: carrierConfig.organizationPath,
  submitter_id: carrierConfig.submitterId.slice(0, SUBMITTER_ID_MAX_LENGTH),
  description: carrierConfig.description,
  service_date_from: carrierConfig.serviceDateFrom as unknown as Date,
  purpose_type: carrierConfig.purposeType as unknown as ISaveCarrierConfigDtoPurposeTypeEnum,
  amount_types: carrierConfig.amountTypes as unknown as ISaveCarrierConfigDtoAmountTypesEnum[],
  status_type: ISaveCarrierConfigDtoStatusTypeEnum.ACTIVE,

  ...(carrierConfig.serviceDateTo
    ? {
        service_date_to: carrierConfig.serviceDateTo,
      }
    : {}),

  ...(carrierConfig.purposeType === PorposeType.adjudication
    ? {}
    : {
        ...(carrierConfig.claimAutopayType
          ? {
              claim_autopay_type: carrierConfig.claimAutopayType,
            }
          : {}),
        is_dependent_deidentified: carrierConfig.isDependentDeidentified || false,
        is_dependent_autocreated: carrierConfig.isDependentDeidentified
          ? false
          : carrierConfig.isDependentAutocreated || false,
        ...(carrierConfig.claimPayeeType
          ? {
              claim_payee_type: carrierConfig.claimPayeeType,
            }
          : {}),
      }),
});

const useCreateCarrierConfigQuery = (onDateOverlapped: () => void, onSuccess: () => void) => {
  const { carrierConfigs, setCarrierConfigs } = useCarrierConfigStore();
  const { mutateAsync, isLoading } = useMutation(
    (value: CarrierConfig) =>
      api.post<ISaveCarrierConfigDto, ICarrierConfigDto>(API_URL.CARRIER_CONFIGS, formatDataToPayload(value)),
    {
      onSuccess: (response, payload) => {
        const { data } = response as AxiosResponse<ICarrierConfigDto>;
        const { organizationName } = payload;

        const createdCarrierConfig = {
          // @ts-ignore
          carrierName: data?.carrier?.name || '',
          carrierExternalId: toString(data?.carrier?.external_id),
          // @ts-ignore
          carrierConfigId: toString(data.id),
          submitterId: data.submitter_id || '',
          organization: organizationName,
          purposeType: capitalize(data.purpose_type).replace(/_/g, ' ') as PorposeType,
          description: data.description || '',
          statusType: data.status_type,
        };

        setCarrierConfigs([createdCarrierConfig, ...carrierConfigs]);
        onSuccess();
      },
      // TODO: Fix type
      // @ts-ignore
      onError: ({
        response: {
          data: { elevate_error_message },
        },
      }) => {
        if (elevate_error_message.includes(DATE_OVERLAPPED_MESSAGE)) {
          onDateOverlapped();
        }
      },
    }
  );

  const handleSave = useCallback(
    async (value: Partial<CarrierConfig>) => {
      await mutateAsync(value as CarrierConfig);
    },
    [mutateAsync]
  );

  return {
    isLoading,
    create: handleSave,
  };
};

export default useCreateCarrierConfigQuery;
